/**
 * Prompt-writing hints (#1462) — pure helpers behind the discovery dialog's
 * "not sure what to type?" guidance.
 *
 * No AI, nothing persisted: these just give a first-timer a starting point so
 * the mandatory Agent Prompt box is never a blank stare. Tailoring uses only
 * data already on hand (the app's domain label + the pages the scan found);
 * absent that, everything degrades to generic copy.
 */

/** The kind-of-coverage chips + the starter phrase each one seeds. */
export const KIND_OF_TESTS: ReadonlyArray<{ label: string; starter: string }> = [
  { label: 'Happy path', starter: 'Create happy-path test cases for ' },
  { label: 'Negative', starter: 'Create negative test cases for ' },
  { label: 'Edge cases', starter: 'Create edge-case test cases for ' },
]

/** Worked prompt examples (#1494) — a few full, good prompts a first-timer
 * can read to learn what to write, each paired with the steps it would cover
 * so the granularity is concrete. The `prompt` is what "Use this" drops into
 * the box; the `steps` are illustrative ("here's what this test would do") and
 * are NOT inserted — a good OttoTester prompt describes the flow + what should
 * pass, and the agent works out the literal clicks. Deliberately generic so
 * they read sensibly for any app; tailoring by domain can come later. */
export interface PromptExample {
  /** Coverage kind — drives the colored badge. */
  kind: 'happy' | 'negative' | 'edge'
  label: string
  /** The good-prompt sentence shown and inserted on "Use this". */
  prompt: string
  /** The flow the prompt would exercise, shown as "Covers: a → b → c". */
  steps: string[]
}

export const PROMPT_EXAMPLES: ReadonlyArray<PromptExample> = [
  {
    kind: 'happy',
    label: 'Happy path',
    prompt: 'Start on the home page, search for an item, open the first result, and confirm its details page opens.',
    steps: ['Open the home page', 'Type an item name in search', 'Click Search', 'Open the first result', 'Confirm the details page loads'],
  },
  {
    kind: 'negative',
    label: 'Negative',
    prompt: 'On the sign-in page, enter a valid email with the wrong password and confirm an error appears.',
    steps: ['Open the sign-in page', 'Enter a valid email with the wrong password', 'Click Sign in', 'Confirm an error message appears'],
  },
  {
    kind: 'edge',
    label: 'Edge case',
    prompt: 'On the search page, search for something with no matches and confirm an empty-results message shows.',
    steps: ['Open the search page', 'Search for a term with no results', 'Confirm a “no results” message appears'],
  },
]

export function buildPromptHintExample(domain?: string): string {
  const cleanDomain = domain?.trim()

  // Took a list of "areas" from the scanner's discovered pages until that
  // extraction was removed — it read every anchor on the landing page, so the
  // areas were link prose rather than areas. Domain-only tailoring is what is
  // left, and it degrades to fully generic copy.
  if (cleanDomain) {
    return `Create happy-path test cases for the main flows of your ${cleanDomain} app, plus a negative test for invalid input.`
  }
  return 'Create happy-path test cases for sign-in, search, and checkout — plus a negative test for signing in with the wrong password.'
}
